  import PageWithTabs from "components/PageWithTabs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OrderFormDetails from "./OrderFormDetails";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import IconButton from "components/blocks/IconButton";
import { useNavigate } from "react-router-dom";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useOrderForm from "./useOrderForm";
import useDeanOfScientificResearchActions from "./useDeanOfScientificResearchActions";
import ReturnOrRejectUpdateOrderModal from "./ReturnOrRejectUpdateOrderModal";
import Modal from "components/blocks/Modal";

export const OrderFormTypes = {
  EditOrder: "EditOrder",
  ReturnedOrder: "ReturnedOrder",
  AddOrder: "AddOrder",
  EditOrderByDeanOfScientificResearch: "EditOrderByDeanOfScientificResearch",
} as const;

export type AddEditFormRef = {
  submitForm: (isDraft: boolean, orderFormType: keyof typeof OrderFormTypes) => void;
  validateForm: () => Promise<boolean>;
};

const OrderForm = ({ orderFormType,isReadonly=false ,isFileReadonly=false}: { orderFormType: keyof typeof OrderFormTypes ,isReadonly?:boolean,isFileReadonly?:boolean }) => {
  const { setBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["Orders", "Common"]);
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const formRef = useRef<AddEditFormRef>(null);
  const { isLoading, serialGID, order, orderProgram, actionNames ,activityGuid} = useOrderForm({ orderFormType });
  const { loading, returnOrRejectProgramOrderUpdateRequest, rejectReturnModalProps, setRejectReturnModalProps } =
    useDeanOfScientificResearchActions({
      orderId: order?.id ?? 0,
      serialGuid: serialGID ?? "",
     activityGuid: activityGuid ?? "",
    });

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingSubmitDraft, setPendingSubmitDraft] = useState(false);

  const onSubmit = async (isDraft = false) => {
    // Validate form before showing confirmation modal
    if (formRef.current) {
      const isValid = await formRef.current.validateForm();

      if (!isValid) {
        // Validation failed - don't show modal
        return;
      }
    }

    // Show confirmation for all order types when submitting (not for draft)
    if (!isDraft && !showConfirmationModal) {
      setPendingSubmitDraft(isDraft);
      setShowConfirmationModal(true);
      return;
    }

    if (formRef.current) {
      formRef.current.submitForm(isDraft, orderFormType);
    }
    setShowConfirmationModal(false);
  };

  const onSaveAsDraft = async () => {
    onSubmit(true);
  };

  const programDetails = orderProgram?.programDetails.find(
    (item) => i18n.language === item.locale?.toLocaleLowerCase(),
  );

  const isReturnedOrder = orderFormType === OrderFormTypes.ReturnedOrder;
  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: isReturnedOrder ? "tasks" : "allOrders",
        path: isReturnedOrder ? "/home/tasks" : "/home/orders",
        menuItemId: isReturnedOrder ? "tasks" : "allOrders",
      },
      {
        localizationKey: order?.id ? "editOrder" : "addOrder",
        path:
          OrderFormTypes.EditOrder === orderFormType
            ? `/home/orders/edit/${order?.id}`
            : isReturnedOrder
            ? `/home/orders/editOrderTask/${serialGID}`
            : "/home/orders/add",
        localizationParameters: { orderNumber: order?.serialNumber },
      },
    ]);
  }, [order]);
  

  return (
    <>
      <PageWithTabs
        loading={isLoading}
        title={
          <div className="d-flex gap-2">
            <span>{programDetails?.displayName ?? ""}</span>
            <IconButton
              icon="icon-view"
              size="lg"
              fitToIconWidth
              innerIconColor="primary"
              iconPosition="start"
              onClick={() => {
                if (!!orderProgram?.id) navigate(`/programDetails/${orderProgram.id}`);
              }}
            />
          </div>
        }
        subTitle={
          <span>
            {t("programNumber")}: {orderProgram?.serialNumber ?? ""} • {t("cycle")}:
            {` ${orderProgram?.programCycleDetail.displayName ?? ""}-${orderProgram?.programCycleYearDetail.displayName ?? ""}`}
          </span>
        }
        titleImgSrc={orderProgram?.adsImage}
        pageHeaders={[
          {
            title: t("startContract"),
            value: toSystemThemeDateFormat(orderProgram?.contractStartDate),
          },
          {
            title: t("fundingTime"),
            value: orderProgram?.fundingDurationInMonths ?? "" + " " + t("month"),
          },
        ]}
        actions={() => {
          return (
            <>
              {orderFormType !== OrderFormTypes.EditOrderByDeanOfScientificResearch && (
                <>
                  <button className="btn btn-primary p-10-32P" onClick={() => onSubmit(false)}>
                    {!isReturnedOrder ? t("submitOrder") : t("send")}
                  </button>
                  {!isReturnedOrder && (
                    <button type="button" className="btn border bg-white text-dark p-10-32P" onClick={onSaveAsDraft}>
                      {t("saveAsDraft")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn border bg-white text-dark p-10-32P"
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    {t("Common:cancel")}
                  </button>
                </>
              )}

              {
                // Dean of scientific research actions
                orderFormType === OrderFormTypes.EditOrderByDeanOfScientificResearch && (
                  <>
                    <>
                      {actionNames?.some((ele) => ele.toLowerCase().includes("approve")) && (
                        <button
                          className="btn btn-primary px-3"
                          onClick={() => {
                            onSubmit(false);
                          }}
                        >
                          {t("approval")}
                        </button>
                      )}

                      {actionNames?.some((ele) => ele.toLowerCase().includes("reject")) && (
                        <button
                          className="btn btn-outline-secondary px-3"
                          onClick={() =>
                            setRejectReturnModalProps({
                              isOpen: true,
                              isReturn: false,
                            })
                          }
                        >
                          {t("reject", {
                            placeholder: t("updateRequest"),
                          })}
                        </button>
                      )}
                      {actionNames?.some((ele) => ele.toLowerCase().includes("return")) && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3"
                          onClick={() =>
                            setRejectReturnModalProps({
                              isOpen: true,
                              isReturn: true,
                            })
                          }
                        >
                          <i className="icon-turn-order" />
                          {t("returnOrder")}
                        </button>
                      )}
                    </>
                  </>
                )
              }
            </>
          );
        }}
        tapContent={
          <OrderFormDetails
            ref={formRef}
            program={orderProgram}
            order={order}
            orderFormType={orderFormType}
            serialGuid={serialGID}
            activityGuid={activityGuid}
            isReadonly={isReadonly}
            isFileReadonly={isFileReadonly}
          />
        }
      />
      <ReturnOrRejectUpdateOrderModal
        isOpen={rejectReturnModalProps.isOpen}
        isReturn={rejectReturnModalProps.isReturn}
        onClose={() => {
          setRejectReturnModalProps({ isOpen: false, isReturn: false });
        }}
        onSubmit={returnOrRejectProgramOrderUpdateRequest}
        isSubmitting={loading}
      />

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
          setPendingSubmitDraft(false);
        }}
        title={t("confirmSubmission")}
        size="lg"
        containerClassName="bg-white pb-2"
        titleClassName="fs-6 text-dark"
        withHeaderBorder
      >
        <div className="py-4 text-center">
          <p className="fs-5">
            {orderFormType === OrderFormTypes.AddOrder
              ? t("areYouSureSubmitOrder")
              : t("areYouSureUpdateOrder")}
          </p>
        </div>

        <div className="divider mt-3"></div>

        <div className="d-flex justify-content-center gap-2 w-100">
          <button
            type="button"
            className="btn text-dark rounded-3 p-10-32P"
            onClick={() => {
              setShowConfirmationModal(false);
              setPendingSubmitDraft(false);
            }}
          >
            {t("no")}
          </button>

          <button
            type="button"
            className="btn btn-primary rounded-3 p-10-32P"
            onClick={() => {
              onSubmit(pendingSubmitDraft);
            }}
          >
            {t("yes")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default OrderForm;
